import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AgentChart from './AgentChart';
import {
  FaRobot, FaPaperPlane, FaCheck, FaTimes, FaSearch, FaExclamationTriangle,
  FaRegLightbulb, FaSpinner, FaUserTie, FaListUl,
} from 'react-icons/fa';
import friendlyError from '../../utils/friendlyError';
import {
  TechShell, TechPageHeader, TechSection, TechTextarea, TechButton, TechButtonGhost,
  StatusPill, TechChip, ErrorBox, borderSoft, mono, panelDeep,
} from '../../styles/techComponents';
import { askAgent, executeProposal } from '../../services/agentService';

/* ============================================================
   Agente de la plataforma — chat que consulta y, con confirmación,
   modifica. A diferencia del Copiloto (que devuelve borradores de
   texto), acá el agente encadena consultas reales y puede escribir.
   ============================================================ */

const Chat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  min-height: 320px;
  max-height: 58vh;
  overflow-y: auto;
  padding: 0.4rem 0.2rem;
`;

const Bubble = styled.div`
  align-self: ${({ $me }) => ($me ? 'flex-end' : 'flex-start')};
  max-width: ${({ $me }) => ($me ? '78%' : '100%')};
  padding: 0.7rem 0.9rem;
  border-radius: 10px;
  font-size: 0.9rem;
  line-height: 1.55;
  white-space: ${({ $me }) => ($me ? 'pre-wrap' : 'normal')};
  background: ${({ $me }) => ($me ? 'rgba(0, 180, 216, 0.12)' : panelDeep)};
  border: 1px solid ${({ $me }) => ($me ? 'rgba(0, 180, 216, 0.3)' : borderSoft)};
  color: var(--text-primary);

  /* Estilos del markdown que devuelve el agente. */
  > *:first-child { margin-top: 0; }
  > *:last-child { margin-bottom: 0; }

  h2, h3 {
    font-size: 0.95rem;
    margin: 1.1rem 0 0.45rem;
    color: var(--text-primary);
  }
  p { margin: 0 0 0.7rem; }
  strong { color: var(--text-primary); font-weight: 600; }
  ul, ol { margin: 0 0 0.7rem; padding-left: 1.2rem; }
  li { margin-bottom: 0.3rem; }
  code {
    font-family: ${mono};
    font-size: 0.82em;
    background: rgba(255, 255, 255, 0.06);
    padding: 1px 5px;
    border-radius: 4px;
  }
  a { color: var(--btn-primary); }
  blockquote {
    margin: 0 0 0.7rem;
    padding-left: 0.7rem;
    border-left: 2px solid ${borderSoft};
    color: var(--text-secondary);
  }

  /* Las tablas se van de ancho en móvil: scrollean solas, no la página. */
  .tabla-wrap { overflow-x: auto; margin: 0 0 0.8rem; }
  table { border-collapse: collapse; width: 100%; font-size: 0.84rem; }
  th, td {
    border: 1px solid ${borderSoft};
    padding: 0.45rem 0.6rem;
    text-align: left;
    vertical-align: top;
  }
  th {
    background: rgba(255, 255, 255, 0.04);
    font-family: ${mono};
    font-size: 0.68rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-secondary);
    font-weight: 500;
  }
`;

// Resumen de una línea bajo cada respuesta. El detalle va al popup.
const TraceBar = styled.div`
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-wrap: wrap;
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-disabled);
  padding: 0.1rem 0 0.2rem;

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-family: inherit;
    font-size: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    padding: 0.25rem 0.6rem;
    border-radius: 999px;
    border: 1px solid ${borderSoft};
    background: transparent;
    color: var(--btn-primary);
    cursor: pointer;
    transition: 0.15s;

    &:hover { border-color: rgba(0, 180, 216, 0.45); background: rgba(0, 180, 216, 0.08); }
  }
`;

// Popup con todo el material: qué consultó, qué dijo cada especialista y las
// fuentes una por una.
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(4, 8, 14, 0.72);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
`;

const Modal = styled.div`
  width: min(920px, 100%);
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  background: var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  overflow: hidden;

  header {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 0.9rem 1.1rem;
    border-bottom: 1px solid ${borderSoft};

    h3 { margin: 0; font-size: 1rem; color: var(--text-primary); }
    .sub {
      font-family: ${mono};
      font-size: 0.62rem;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--text-disabled);
    }
    button {
      margin-left: auto;
      background: transparent;
      border: 1px solid ${borderSoft};
      border-radius: 6px;
      color: var(--text-secondary);
      width: 30px; height: 30px;
      cursor: pointer;
      &:hover { color: var(--alert-error); border-color: rgba(239, 71, 111, 0.4); }
    }
  }
  .body { overflow-y: auto; padding: 0.9rem 1.1rem 1.2rem; }
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.4rem;
  padding: 0.7rem 1.1rem 0;
  flex-wrap: wrap;

  button {
    font-family: ${mono};
    font-size: 0.64rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    padding: 0.35rem 0.7rem;
    border-radius: 999px;
    cursor: pointer;
    border: 1px solid ${({ theme }) => theme || 'rgba(255,255,255,0.06)'};
    background: ${({ $on }) => ($on ? 'rgba(0,180,216,0.14)' : 'transparent')};
    color: var(--text-secondary);
    &[data-on='true'] { color: var(--btn-primary); border-color: rgba(0, 180, 216, 0.45); }
  }
`;

// Bloque reusable dentro del popup.
const Trace = styled.div`
  max-width: 100%;
  font-family: ${mono};
  font-size: 0.7rem;
  color: var(--text-disabled);
  .step {
    padding: 0.4rem 0 0.4rem 0.8rem;
    border-left: 1px solid ${borderSoft};
    margin-bottom: 0.2rem;
  }
  .head { display: flex; align-items: center; gap: 0.45rem; flex-wrap: wrap; }
  .head b { color: var(--text-secondary); font-weight: 500; }
  .head .res { color: var(--text-disabled); }

  /* El hallazgo del especialista se lee: sale de la monoespaciada chica. */
  .hallazgo {
    font-family: var(--font-sans, inherit);
    font-size: 0.83rem;
    line-height: 1.5;
    color: var(--text-secondary);
    margin: 0.45rem 0 0.2rem;
    padding: 0.5rem 0.7rem;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 6px;
    text-transform: none;
    letter-spacing: 0;
  }
  .hallazgo strong { color: var(--text-primary); }
  /* Sin esto los títulos del especialista salen con el tamaño por defecto del
     navegador y quedan más grandes que la respuesta principal. */
  .hallazgo h1, .hallazgo h2, .hallazgo h3, .hallazgo h4 {
    font-size: 0.86rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0.7rem 0 0.35rem;
    line-height: 1.35;
  }
  .hallazgo h1:first-child, .hallazgo h2:first-child, .hallazgo h3:first-child { margin-top: 0; }
  .hallazgo p { margin: 0 0 0.5rem; }
  .hallazgo ul, .hallazgo ol { margin: 0 0 0.5rem; padding-left: 1.1rem; }
  .hallazgo li { margin-bottom: 0.2rem; }
  .hallazgo .tabla-wrap { overflow-x: auto; margin: 0 0 0.5rem; }
  .hallazgo table { border-collapse: collapse; width: 100%; font-size: 0.78rem; }
  .hallazgo th, .hallazgo td {
    border: 1px solid ${borderSoft};
    padding: 0.3rem 0.45rem;
    text-align: left;
    vertical-align: top;
  }
  .hallazgo th { background: rgba(255, 255, 255, 0.04); color: var(--text-secondary); font-weight: 500; }
  .hallazgo p:last-child { margin-bottom: 0; }

  .fuentes {
    list-style: none;
    margin: 0.35rem 0 0;
    padding: 0;
  }
  .fuentes li { padding: 0.2rem 0; }
  .fuentes a { color: var(--btn-primary); text-decoration: none; }
  .fuentes a:hover { text-decoration: underline; }
  .fuentes .tit { color: var(--text-secondary); }
  .fuentes .det { color: var(--text-disabled); }
  .fuentes .ext {
    color: var(--text-disabled);
    opacity: 0.75;
    padding-left: 0.6rem;
    margin-top: 2px;
    text-transform: none;
    letter-spacing: 0;
  }
`;

const ProposalCard = styled.div`
  align-self: flex-start;
  width: 100%;
  border: 1px solid ${({ $done }) => ($done ? 'rgba(6, 214, 160, 0.4)' : 'rgba(255, 209, 102, 0.45)')};
  border-left: 3px solid ${({ $done }) => ($done ? 'var(--alert-success)' : 'var(--alert-warning)')};
  border-radius: 8px;
  background: rgba(11, 19, 32, 0.6);
  padding: 0.8rem 0.9rem;

  .tag {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${({ $done }) => ($done ? 'var(--alert-success)' : 'var(--alert-warning)')};
  }
  .titulo { font-size: 0.92rem; font-weight: 500; color: var(--text-primary); margin-top: 3px; }
  .detalle { font-size: 0.82rem; color: var(--text-secondary); margin-top: 2px; }
  .acciones { display: flex; gap: 0.5rem; margin-top: 0.7rem; flex-wrap: wrap; }
  .resultado { font-size: 0.8rem; color: var(--alert-success); margin-top: 0.5rem; }
  .fallo { font-size: 0.8rem; color: var(--alert-error); margin-top: 0.5rem; }
`;

const Composer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
  margin-top: 0.9rem;
  padding-top: 0.9rem;
  border-top: 1px solid ${borderSoft};

  textarea { flex: 1; min-height: 52px; resize: vertical; }
`;

const Examples = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-bottom: 0.9rem;
`;

const ExampleChip = styled.button`
  font-family: inherit;
  font-size: 0.8rem;
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
  border: 1px solid ${borderSoft};
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  transition: 0.15s;
  &:hover { color: var(--text-primary); border-color: rgba(0, 180, 216, 0.4); }
`;

// Render del markdown del agente. Intercepta los bloques ```grafico para
// dibujarlos, y envuelve las tablas para que scrolleen ellas y no la página.
const MD_COMPONENTS = {
  code({ inline, className, children, ...props }) {
    const lang = /language-(\w+)/.exec(className || '')?.[1];
    if (!inline && lang === 'grafico') {
      return <AgentChart raw={String(children).replace(/\n$/, '')} />;
    }
    return <code className={className} {...props}>{children}</code>;
  },
  table({ children }) {
    return <div className="tabla-wrap"><table>{children}</table></div>;
  },
  a({ href, children }) {
    return <a href={href} target="_blank" rel="noreferrer">{children}</a>;
  },
};

const Respuesta = ({ texto }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
    {texto}
  </ReactMarkdown>
);

// Una línea: cuántos especialistas, consultas y fuentes distintas.
function resumirTraza(steps) {
  const expertos = steps.filter((s) => s.tipo === 'especialista').length;
  const consultas = steps.filter((s) => s.tipo === 'consulta').length;
  const fuentes = dedupeFuentes(steps.flatMap((s) => s.fuentes || [])).length;
  return [
    expertos ? `${expertos} especialista${expertos === 1 ? '' : 's'}` : null,
    consultas ? `${consultas} consulta${consultas === 1 ? '' : 's'}` : null,
    fuentes ? `${fuentes} fuente${fuentes === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');
}

// La misma fuente aparece muchas veces entre consultas: se muestra una sola.
function dedupeFuentes(fuentes) {
  const vistas = new Set();
  return fuentes.filter((f) => {
    const clave = `${f.titulo}|${f.detalle || ''}`;
    if (vistas.has(clave)) return false;
    vistas.add(clave);
    return true;
  });
}

const FuenteItem = ({ f }) => (
  <li>
    {f.url
      ? <a href={f.url} target="_blank" rel="noreferrer">{f.titulo}</a>
      : <span className="tit">{f.titulo}</span>}
    {f.detalle && <span className="det"> · {f.detalle}</span>}
    {f.extracto && <div className="ext">{f.extracto}…</div>}
  </li>
);

const DetalleModal = ({ turno, onClose }) => {
  const [tab, setTab] = useState('proceso');
  const especialistas = turno.steps.filter((s) => s.tipo === 'especialista');
  const fuentes = dedupeFuentes(turno.steps.flatMap((s) => s.fuentes || []));

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const TABS = [
    { id: 'proceso', label: `Proceso (${turno.steps.length})` },
    especialistas.length ? { id: 'especialistas', label: `Especialistas (${especialistas.length})` } : null,
    fuentes.length ? { id: 'fuentes', label: `Fuentes (${fuentes.length})` } : null,
  ].filter(Boolean);

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <header>
          <div>
            <h3>De dónde salió esta respuesta</h3>
            <span className="sub">{resumirTraza(turno.steps)}</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar"><FaTimes /></button>
        </header>

        <Tabs>
          {TABS.map((t) => (
            <button key={t.id} type="button" data-on={tab === t.id} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </Tabs>

        <div className="body">
          {tab === 'proceso' && (
            <Trace>
              {turno.steps.map((s, i) => {
                const Icon = STEP_ICON[s.tipo] || FaSearch;
                return (
                  <div className="step" key={i}>
                    <div className="head">
                      <Icon style={{ fontSize: '0.62rem' }} />
                      <b>{s.herramienta}</b>
                      {s.resumen && <span className="res">· {s.resumen}</span>}
                    </div>
                  </div>
                );
              })}
            </Trace>
          )}

          {tab === 'especialistas' && (
            <Trace>
              {especialistas.map((s, i) => (
                <div className="step" key={i}>
                  <div className="head"><FaUserTie style={{ fontSize: '0.62rem' }} /><b>{s.herramienta}</b></div>
                  <div className="hallazgo"><Respuesta texto={s.hallazgo} /></div>
                </div>
              ))}
            </Trace>
          )}

          {tab === 'fuentes' && (
            <Trace>
              <ul className="fuentes">
                {fuentes.map((f, i) => <FuenteItem key={i} f={f} />)}
              </ul>
            </Trace>
          )}
        </div>
      </Modal>
    </Overlay>
  );
};

const EJEMPLOS = [
  '¿Cómo viene mi imagen este mes?',
  '¿Qué pasó con mis proyectos en el Concejo?',
  '¿Qué barrio tengo más descuidado?',
  '¿Qué se dice de mí en redes?',
  'Redactá un comunicado sobre las clausuras',
  'Compará mi posición con la de los Passaglia',
];

const STEP_ICON = {
  consulta: FaSearch,
  especialista: FaUserTie,
  propuesta: FaRegLightbulb,
  error: FaExclamationTriangle,
};

export default function Agente() {
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Estado por propuesta: { [key]: { estado: 'pendiente'|'aplicando'|'hecho'|'error', mensaje } }
  const [proposalState, setProposalState] = useState({});
  // Turno cuyo detalle se está mirando en el popup (null = cerrado).
  const [detalle, setDetalle] = useState(null);
  const chatRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [turns, loading, proposalState]);

  const send = useCallback(async (text) => {
    const pregunta = String(text || '').trim();
    if (!pregunta || loading) return;

    setError(null);
    const nuevos = [...turns, { role: 'user', content: pregunta }];
    setTurns(nuevos);
    setInput('');
    setLoading(true);

    try {
      // Se manda solo role/content: steps y proposals son de la UI.
      const historia = nuevos.map((t) => ({ role: t.role, content: t.content }));
      const data = await askAgent(historia);
      setTurns((prev) => [...prev, {
        role: 'assistant',
        content: data.answer,
        steps: data.steps || [],
        proposals: data.proposals || [],
      }]);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [turns, loading]);

  const confirmar = async (key, proposal) => {
    setProposalState((p) => ({ ...p, [key]: { estado: 'aplicando' } }));
    try {
      const r = await executeProposal({ tool: proposal.tool, args: proposal.args });
      setProposalState((p) => ({
        ...p,
        [key]: { estado: 'hecho', mensaje: r.titulo || 'Aplicado' },
      }));
    } catch (e) {
      setProposalState((p) => ({
        ...p,
        [key]: { estado: 'error', mensaje: e?.response?.data?.error || friendlyError(e) },
      }));
    }
  };

  const descartar = (key) => {
    setProposalState((p) => ({ ...p, [key]: { estado: 'descartado' } }));
  };

  return (
    <TechShell>
      <TechPageHeader
        title="Agente de la plataforma"
        dossier="AGENTE"
        subtitle="Preguntale lo que necesites sobre la operación. Busca los datos por su cuenta, cruza secciones y, si le pedís un cambio, lo prepara para que lo confirmes."
        actions={turns.length > 0 && (
          <TechButtonGhost onClick={() => { setTurns([]); setProposalState({}); setError(null); }}>
            Empezar de nuevo
          </TechButtonGhost>
        )}
      >
        <StatusPill>Nada se guarda sin tu confirmación</StatusPill>
        {/* El contrapunto del aviso que está en el Asistente Arcana: acá se
            tarda más porque consulta varias veces. */}
        <StatusPill>
          Tarda más que el Asistente Arcana porque consulta varias secciones ·{' '}
          <Link to="/chat" style={{ color: 'var(--btn-primary)' }}>ir al chat rápido</Link>
        </StatusPill>
      </TechPageHeader>

      <TechSection title="Conversación" icon={<FaRobot />}>
        {turns.length === 0 && (
          <Examples>
            {EJEMPLOS.map((e) => (
              <ExampleChip key={e} type="button" onClick={() => send(e)}>{e}</ExampleChip>
            ))}
          </Examples>
        )}

        <Chat ref={chatRef}>
          {turns.map((t, i) => (
            <React.Fragment key={i}>
              <Bubble $me={t.role === 'user'}>
                {t.role === 'user' ? t.content : <Respuesta texto={t.content} />}
              </Bubble>

              {/* Resumen de una línea. El material entero vive en el popup:
                  volcado inline tapaba la respuesta con cientos de fuentes. */}
              {t.steps?.length > 0 && (
                <TraceBar>
                  <span className="txt">{resumirTraza(t.steps)}</span>
                  <button type="button" onClick={() => setDetalle(t)}>
                    <FaListUl /> Ver los datos
                  </button>
                </TraceBar>
              )}

              {t.proposals?.map((p, j) => {
                const key = `${i}-${j}-${p.tool}`;
                const st = proposalState[key]?.estado || 'pendiente';
                if (st === 'descartado') return null;
                return (
                  <ProposalCard key={key} $done={st === 'hecho'}>
                    <div className="tag">
                      {st === 'hecho' ? 'Aplicado' : 'Requiere tu confirmación'}
                    </div>
                    <div className="titulo">{p.titulo}</div>
                    <div className="detalle">{p.detalle}</div>

                    {st === 'pendiente' && (
                      <div className="acciones">
                        <TechButton type="button" onClick={() => confirmar(key, p)}>
                          <FaCheck /> Confirmar
                        </TechButton>
                        <TechButtonGhost type="button" onClick={() => descartar(key)}>
                          <FaTimes /> Descartar
                        </TechButtonGhost>
                      </div>
                    )}
                    {st === 'aplicando' && (
                      <div className="acciones"><TechChip>Aplicando…</TechChip></div>
                    )}
                    {st === 'hecho' && <div className="resultado">✓ Listo, quedó guardado.</div>}
                    {st === 'error' && (
                      <>
                        <div className="fallo">No se pudo aplicar: {proposalState[key].mensaje}</div>
                        <div className="acciones">
                          <TechButtonGhost type="button" onClick={() => confirmar(key, p)}>
                            Reintentar
                          </TechButtonGhost>
                        </div>
                      </>
                    )}
                  </ProposalCard>
                );
              })}
            </React.Fragment>
          ))}

          {loading && (
            <Bubble>
              <FaSpinner style={{ marginRight: 8 }} />
              Consultando la plataforma. Si el tema lo pide, convoca especialistas
              y puede tardar un minuto.
            </Bubble>
          )}
        </Chat>

        {error && <ErrorBox style={{ marginTop: '0.8rem' }}><FaExclamationTriangle /> {error}</ErrorBox>}

        <Composer>
          <TechTextarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
            }}
            placeholder="Preguntá o pedí un cambio. Enter manda, Shift+Enter hace salto de línea."
            disabled={loading}
          />
          <TechButton type="button" onClick={() => send(input)} disabled={loading || !input.trim()}>
            <FaPaperPlane /> Enviar
          </TechButton>
        </Composer>
      </TechSection>

      {detalle && <DetalleModal turno={detalle} onClose={() => setDetalle(null)} />}
    </TechShell>
  );
}
